import { action, computed, flow, makeObservable, observable, override } from 'mobx';
import { CancellablePromise } from 'mobx/dist/internal';
import { userStore } from '../userStore';
import { BaseOrdersStore } from './baseOrdersStore';
import { OrderTabs } from './types';
import { ORDER_STATUS } from '~/constants/order';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';
import { orderCast, TOrder, TOrderCRUDEvents } from '~/types/orders';

export type CallCenterTab = OrderTabs.All | OrderTabs.Draft | OrderTabs.OnHold | OrderTabs.Active | OrderTabs.Closed | OrderTabs.ActionRequired;

const tabs = {
  [OrderTabs.All]: {
    key: OrderTabs.All,
    label: 'status_all',
    visible: true,
    newOrders: observable.map(),
  },
  [OrderTabs.Draft]: {
    key: OrderTabs.Draft,
    label: 'draft',
    visible: true,
    newOrders: observable.map(),
  },
  [OrderTabs.OnHold]: {
    key: OrderTabs.OnHold,
    label: 'on_hold_tab',
    newOrders: observable.map(),
  },
  [OrderTabs.Active]: {
    key: OrderTabs.Active,
    label: 'active',
    newOrders: observable.map(),
  },
  [OrderTabs.Closed]: {
    key: OrderTabs.Closed,
    label: 'closed_tab',
    newOrders: observable.map(),
  },
  [OrderTabs.ActionRequired]: {
    key: OrderTabs.ActionRequired,
    label: 'action_required_tab',
    newOrders: observable.map(),
  },
};

export const statusByTab = {
  [OrderTabs.All]: [
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.ASSIGNED,
    ORDER_STATUS.CANCELED,
    ORDER_STATUS.COMPLETED,
    ORDER_STATUS.DRAFT,
    ORDER_STATUS.ON_HOLD,
    ORDER_STATUS.OUT_FOR_DELIVERY,
    ORDER_STATUS.PENDING,
    ORDER_STATUS.PICKEDUP,
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.RETURNED,
    ORDER_STATUS.WAITING_FOR_PAYMENT,
    ORDER_STATUS.WAITING_FOR_PICKUP,
  ],
  [OrderTabs.Draft]: [ORDER_STATUS.DRAFT],
  [OrderTabs.OnHold]: [ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD],
  [OrderTabs.Active]: [
    ORDER_STATUS.PENDING,
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.ASSIGNED,
    ORDER_STATUS.PICKEDUP,
    ORDER_STATUS.OUT_FOR_DELIVERY,
  ],
  [OrderTabs.Closed]: [ORDER_STATUS.CANCELED, ORDER_STATUS.COMPLETED],
  [OrderTabs.ActionRequired]: [ORDER_STATUS.RETURNED],
};

const getFilterByTab = (tab: CallCenterTab) => {
  return {
    status: statusByTab[tab],
  };
};

export class CallCenterStore extends BaseOrdersStore {
  constructor() {
    super();
    makeObservable(this, {
      _checkOrderMeetsFilters: override,
      _makeFetchOrdersParams: override,
      _tabs: observable,
      activeTabKey: observable,
      checkOrderMeetsCurrentTabAndFilters: override,
      createDraft: action,
      deleteDraft: action,
      handleUpdateOrder: override,
      hideOrderList: action,
      orderListVisible: observable,
      setActiveTab: action,
      showOrderList: action,
      tabs: computed,
      draftLoadingStatus: observable,
    });
  }

  isCallCenterScreen = true;

  orderListVisible = true;

  _tabs = tabs;
  activeTabKey: CallCenterTab = OrderTabs.All;

  private createDraftAbortController = new AbortController();
  private deleteDraftAbortController = new AbortController();

  public draftLoadingStatus = LoadingStatus.Init;

  public createDraft: () => CancellablePromise<string | any> = flow(function* (this: CallCenterStore): any {
    this.createDraftAbortController.abort();
    const createDraftAbortController = new AbortController();
    this.createDraftAbortController = createDraftAbortController;

    this.draftLoadingStatus = LoadingStatus.Loading;

    try {
      const response = yield client.get('/v3/orders/draft');
      this.draftLoadingStatus = LoadingStatus.Success;

      return String(response.order.id);
    } catch (error: any) {
      if (createDraftAbortController.signal.aborted) {
        this.draftLoadingStatus = LoadingStatus.Aborted;
      } else {
        this.draftLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'callCenterStore createDraft' });
      }
      return null;
    }
  }).bind(this);

  public deleteDraft = flow(function* (this: CallCenterStore, orderId: string) {
    this.deleteDraftAbortController.abort();
    const deleteDraftAbortController = new AbortController();
    this.deleteDraftAbortController = deleteDraftAbortController;

    this.draftLoadingStatus = LoadingStatus.Loading;

    try {
      yield client.delete(`/v3/orders/${orderId}`);
      this.draftLoadingStatus = LoadingStatus.Success;

      this.deleteOrder(orderId);
    } catch (error: any) {
      if (deleteDraftAbortController.signal.aborted) {
        this.draftLoadingStatus = LoadingStatus.Aborted;
      } else {
        this.draftLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'callCenterStore deleteDraft' });
      }
    }
  }).bind(this);

  setActiveTab = (activeTab: CallCenterTab) => {
    this.activeTabKey = activeTab;
    this._getOrdersWithNoDelay(0);
  };

  _makeFetchOrdersParams(offset?: number, per_page: number = 25) {
    const branches = userStore.user.branches.map(({ id }) => id);

    let currentOffset = 0;
    if (typeof offset === 'number') {
      currentOffset = offset;
    } else if (typeof this.currentOffset === 'number') {
      currentOffset = this.currentOffset + per_page;
    }

    return this.searchValue
      ? {
          ...getFilterByTab(OrderTabs.All),
          offset: currentOffset,
          per_page,
          branches,
          search: this.searchValue,
          is_delete: 0,
        }
      : {
          ...getFilterByTab(this.activeTabKey),
          offset: currentOffset,
          per_page,
          branches,
          is_delete: 0,
        };
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  handleUpdateOrder(data: any, event?: TOrderCRUDEvents) {
    const order = orderCast(data);
    const orderMeetsCurrentTabAndFilters = this.checkOrderMeetsCurrentTabAndFilters(order);

    this._updateOrder(order, orderMeetsCurrentTabAndFilters);
    return;
  }

  checkOrderMeetsCurrentTabAndFilters(order: TOrder) {
    const orderMeetsFilters = this._checkOrderMeetsFilters(order);
    const { status } = getFilterByTab(this.activeTabKey);
    const orderMeetsTabFilter = status.includes(order.order_status_code);

    return orderMeetsFilters && orderMeetsTabFilter;
  }

  get tabs() {
    const tabs = Object.entries(this._tabs);
    return tabs;
  }

  showOrderList = () => {
    this.orderListVisible = true;
  };

  hideOrderList = () => {
    this.orderListVisible = false;
  };
}

export const callCenterStore = new CallCenterStore();
